import React, { useState, useEffect } from 'react';
import { apiGet } from '../api';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  CalendarDays, Clock, Users, Loader2, MapPin
} from 'lucide-react';
import { format } from 'date-fns';
import { formatApiError } from '../lib/apiError';
import { formatActivity } from '../lib/formatActivity';

export const InstructorDashboard = () => {
  const { api, user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const data = await apiGet('/api/instructor/dashboard');
      setDashboard(data);
    } catch (e) {
      console.error('Failed to fetch instructor dashboard:', e);
      setError(formatApiError(e, 'Failed to load dashboard'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Card className="max-w-md">
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground mb-4">{error}</p>
              <p className="text-sm text-muted-foreground">
                If you're an instructor, please contact your studio owner to set up your account.
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const { instructor, studio, upcoming_classes, total_upcoming } = dashboard;

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="instructor-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold mb-2">
            Welcome, {instructor.first_name}!
          </h1>
          <p className="text-muted-foreground">
            Your schedule and class signups at {studio?.name || 'your studio'}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Upcoming Classes</CardTitle>
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{total_upcoming}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Signups</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {upcoming_classes.reduce((sum, cls) => sum + cls.signup_count, 0)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Studio</CardTitle>
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-lg font-medium truncate">{studio?.name || 'N/A'}</div>
            </CardContent>
          </Card>
        </div>

        {/* Your Specialties */}
        {instructor.specialties?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Your Specialties</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {instructor.specialties.map((specialty, i) => (
                  <Badge key={i} variant="secondary">{specialty}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Classes */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Your Upcoming Classes</h2>
          
          {upcoming_classes.length > 0 ? (
            <div className="space-y-4">
              {upcoming_classes.map((cls, i) => (
                <Card key={cls.id} data-testid={`instructor-class-${i}`}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Badge variant="outline">{formatActivity(cls.activity_type) || 'Class'}</Badge>
                          {cls.name}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-4 mt-1">
                          <span className="flex items-center gap-1">
                            <CalendarDays className="w-4 h-4" />
                            {format(new Date(cls.start_time), 'EEEE, MMMM d, yyyy')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {format(new Date(cls.start_time), 'h:mm a')} - {format(new Date(cls.end_time), 'h:mm a')}
                          </span>
                        </CardDescription>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold">{cls.signup_count}/{cls.max_capacity}</p>
                        <p className="text-sm text-muted-foreground">signed up</p>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Attendees ({cls.signups.length})
                    </h4>
                    
                    {cls.signups.length > 0 ? (
                      <div className="grid gap-2">
                        {cls.signups.map((signup, j) => (
                          <div 
                            key={j}
                            className="flex items-center justify-between p-2 rounded-lg bg-muted"
                            data-testid={`class-${i}-signup-${j}`}
                          >
                            <div>
                              <p className="font-medium">{signup.name}</p>
                              <p className="text-sm text-muted-foreground">{signup.email}</p>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Booked {format(new Date(signup.booked_at), 'MMM d, h:mm a')}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground py-2">No signups yet</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center">
              <CalendarDays className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">No upcoming classes scheduled</p>
            </Card>
          )}
        </div>

        <p className="text-xs text-muted-foreground text-center">
          This is a read-only view. Contact your studio owner to make schedule changes.
        </p>
      </div>
    </DashboardLayout>
  );
};
